import { UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import {
  BidsQuery,
  DeleteOpportunityMutation,
  DeleteOpportunityMutationVariables,
  OpportunitiesQuery,
} from "lib/generated/app-service-gql/graphql";
import { graphql } from "../../../generated/app-service-gql";
import { useGraphQlMutation } from "../useGraphQlClient";

const DELETE_OPPORTUNITY_MUTATION = graphql(`
  mutation deleteOpportunity($ids: [String!]!) {
    deleteOpportunity(ids: $ids) {
      ids
      bidIds
    }
  }
`);

export function useDeleteOpportunity(
  options: UseMutationOptions<
    DeleteOpportunityMutation,
    unknown,
    DeleteOpportunityMutationVariables,
    unknown
  >,
) {
  const queryClient = useQueryClient();
  const { data, ...rest } = useGraphQlMutation(DELETE_OPPORTUNITY_MUTATION, {
    ...options,
    onSuccess: (data, variables, context) => {
      // Remove any opportunities in the cache assigned to any of the deleted opportunities
      queryClient.setQueryData(["opportunities"], (prevData: OpportunitiesQuery | undefined) => {
        if (!prevData) return prevData;
        const updatedResults = prevData.opportunities.results.filter(
          (o) => !data.deleteOpportunity.ids.includes(o.id),
        );
        return {
          opportunities: {
            results: updatedResults,
            total: prevData.opportunities.total - updatedResults.length,
          },
        };
      });

      // Remove any bids in the cache assigned to any of the deleted opportunities
      queryClient.setQueryData(["bids"], (prevData: BidsQuery | undefined) => {
        if (!prevData) return prevData;

        const updatedResults = prevData.bids.results.filter(
          (b) => b.opportunity?.id && !data.deleteOpportunity.ids.includes(b.opportunity.id),
        );
        return {
          bids: {
            results: updatedResults,
            totalResults: prevData.bids.totalResults - updatedResults.length,
          },
        };
      });
      void queryClient.invalidateQueries(["bid", data.deleteOpportunity.bidIds]);
      void queryClient.invalidateQueries(["opportunity", variables.ids]);
      void queryClient.invalidateQueries(["notice"]);
      options?.onSuccess?.(data, variables, context);
    },
  });
  return { data, ...rest };
}
